import { Tooltip } from '@material-ui/core';
import Link from '@material-ui/core/Link';
import { SPACES } from 'common/constants/sizes';
import { navigateBookDemoClick } from 'common/utils/utils';
import { DEFAULT_COLORS } from 'components/base/ColorPickerPallete';
import FormBuilder, { FORM_CONTROL_ENUM } from 'components/base/FormBuilder';
import Text from 'components/base/Text';
import { Flex, Padding } from 'components/base/layout-components';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { isTeamSynced as getIsTeamSynced } from 'state/selectors/teams';
import styled from 'styled-components';
import { SYNCED_TEAM_MODEL } from 'views/root/views/home/views/settings/views/ContributorsAndTeams/Teams/teams.model';
import ContributorsTable from './ContributorsTable';
import { submit } from './create-team-modal.service';
import { LBButton } from '@linearb/buzz';

const SelectedTitle = styled.div`
  position: absolute;
  margin-top: 2.5rem;
`;

// eslint-disable-next-line max-lines-per-function
const CreateTeamModal = props => {
  const {
    onCancel,
    team = null,
    title,
    contributors = [],
    isEdit = false,
    state
  } = props;
  const isTeamSynced = getIsTeamSynced(team);

  const { maxContributors } = state;
  const [isValid, setIsValid] = useState(false);
  const [saving, setSaving] = useState(false);
  const initialState = team || {
    name: '',
    initials: '',
    color: DEFAULT_COLORS[_.random(0, DEFAULT_COLORS.length - 1)].hex,
    contributor_ids: []
  };

  const [createTeamForm, setCreateTeamForm] = useState(initialState);
  const [selectedContributorIds, setSelectedContributorIds] = useState(
    initialState?.contributor_ids || []
  );

  const [withHistory, setWithHistory] = useState({
    withHistory: true
  });
  const [withoutBackfill, setWithoutBackfill] = useState({
    withoutBackfill: false
  });

  const contributorsById = _.keyBy(contributors, 'id');
  const selectedContributorsNames =
    selectedContributorIds.map(contributorId =>
      _.get(contributorsById[contributorId], 'name', '')
    ) || [];

  const isNewTeamConfig = {
    type: FORM_CONTROL_ENUM.CHECKBOX,
    key: 'withHistory',
    label: (
      <Text color='gray' size='medium'>
        Get team's history after team is created.
      </Text>
    ),
    validationFunc: () => true,
    gridSize: 12,
    withLabel: false,
    color: 'primary',
    fieldProps: {
      inputProps: {
        'aria-label': 'checkbox'
      }
    }
  };

  const isEditTeamConfig = {
    type: FORM_CONTROL_ENUM.CHECKBOX,
    key: 'withoutBackfill',
    label: (
      <Text color='gray' size='medium'>
        This member just joined the team. (Do not include previous work as part
        of the team's work)
      </Text>
    ),
    validationFunc: () => true,
    gridSize: 12,
    withLabel: false,
    fieldProps: {
      inputProps: {
        'aria-label': 'checkbox'
      }
    }
  };

  const formBuilderConfig = [
    ...(isTeamSynced
      ? [
          {
            type: FORM_CONTROL_ENUM.TEXT,
            key: SYNCED_TEAM_MODEL.SYNCED_PROVIDER_NAME,
            label: 'Github Name',
            validationFunc: val => _.trim(val).length >= 1,
            errMsg: 'Please choose a team name greater than 1 letter',
            gridSize: 8,
            flexProps: {
              pt: 'tiny'
            },
            disabled: true
          }
        ]
      : []),
    {
      type: FORM_CONTROL_ENUM.TEXT,
      key: 'name',
      label: 'Name',
      validationFunc: val => _.trim(val).length >= 1,
      errMsg: 'Please choose a team name greater than 1 letter',
      gridSize: 8,
      flexProps: {
        pt: 'tiny'
      }
    },
    {
      type: FORM_CONTROL_ENUM.COLOR_PICKER,
      key: 'color',
      label: 'Color',
      validationFunc: val => val && !!val.length,
      gridSize: 2,
      flexProps: {
        pt: 'tiny'
      }
    },
    {
      type: FORM_CONTROL_ENUM.TEXT,
      key: 'initials',
      label: 'Initials',
      validationFunc: val => val.length > 0 && val.length <= 3,
      errMsg: 'Please enter between 1 to 3 letters',
      gridSize: 2,
      flexProps: {
        pt: 'tiny'
      }
    }
  ];

  useEffect(() => {
    if (createTeamForm.name && createTeamForm.initials.length < 2) {
      const splittedName = createTeamForm.name.split(' ');
      setCreateTeamForm({
        ...createTeamForm,
        initials: splittedName
          .slice(0, 2)
          .map((word = '') => word.charAt(0).toUpperCase())
          .join('')
      });
    }
  }, [createTeamForm.name]);

  const contributorsLength = selectedContributorIds.length;
  const hasMaxContributors =
    !!maxContributors && contributorsLength === maxContributors;

  const onCheckboxClicked = (id, checked) => {
    setSelectedContributorIds(
      checked
        ? [...selectedContributorIds, id]
        : _.without(selectedContributorIds, id)
    );
  };
  return (
    <Flex
      direction='column'
      justify='space-between'
      width='calc(100% - 4rem)'
      padding='medium'
    >
      <Text size='x-x-large' wieght='400' pb={SPACES.medium}>
        {title}
      </Text>

      <FormBuilder
        fields={formBuilderConfig}
        data={createTeamForm}
        validateOnTouch={false}
        onChange={(d, isValid) => {
          setCreateTeamForm(d);
          setIsValid(isValid && contributorsLength);
        }}
      />

      <Flex grow={1} style={{ overflow: 'hidden' }}>
        <Flex direction='column' width='100%'>
          <Tooltip
            title={selectedContributorsNames.join(', ')}
            placement='right-start'
          >
            <SelectedTitle>
              {!!contributorsLength && !isTeamSynced && (
                <Text size='medium' pb={SPACES.tiny} weight='400' color='gray'>
                  {contributorsLength} selected
                </Text>
              )}
            </SelectedTitle>
          </Tooltip>
          <ContributorsTable
            state={state}
            contributors={contributors}
            onCheckboxClicked={onCheckboxClicked}
            selectedContributorIds={selectedContributorIds}
            readonly={isTeamSynced}
          />
        </Flex>
      </Flex>

      {isTeamSynced ? null : !isEdit ? (
        <FormBuilder
          fields={[isNewTeamConfig]}
          data={withHistory}
          validateOnTouch={false}
          onChange={(d, isValid) => setWithHistory(d)}
        />
      ) : (
        <FormBuilder
          fields={[isEditTeamConfig]}
          data={withoutBackfill}
          validateOnTouch={false}
          onChange={(d, isValid) => setWithoutBackfill(d)}
        />
      )}
      <Flex
        justify={maxContributors ? 'space-between' : 'flex-end'}
        pt='medium'
      >
        <Flex direction='column'>
          {maxContributors && !isTeamSynced && (
            <Text size='medium'>
              {contributorsLength}/{maxContributors}{' '}
              {hasMaxContributors ? 'max' : ''} members selected
            </Text>
          )}
          {hasMaxContributors && !isTeamSynced && (
            <Flex pt='tiny'>
              <Text>Want to add more team members? </Text>&nbsp;
              <Link onClick={navigateBookDemoClick}>upgrade your account</Link>
            </Flex>
          )}
        </Flex>
        <Flex direction='row-reverse'>
          <Padding l='small'>
            <LBButton
              disabled={!isValid || saving}
              onClick={async () => {
                setSaving(true);
                await submit({
                  ...props,
                  isEdit,
                  withHistory,
                  initialState,
                  createTeamForm: {
                    ...createTeamForm,
                    contributor_ids: selectedContributorIds
                  },
                  withoutBackfill
                });
                setSaving(false);
              }}
            >
              Save
            </LBButton>
          </Padding>

          <LBButton variant='outline' onClick={onCancel}>
            Cancel
          </LBButton>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default CreateTeamModal;
